'use client';

import 'swiper/css/pagination';

import type { Swiper as SwiperCore } from 'swiper';
import type { VerticalProductItemProps } from '@weberus/ui/components';
import type { ConvertedProductPageWidgetData } from '@weberus/shared-types';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import { PageController, SubHeader, Tooltip, ProductBadge } from '@yanolja-rnd/nol-design-system';
import { Swiper, SwiperSlide } from 'swiper/react';
import { VerticalProductItem } from '@weberus/ui/components';
import { YaTrackerImpression, clientLogStandardized } from '@weberus/client-tracker';
import { useRef, useState } from 'react';

import { waitUntilClickTimeout } from '@weberus/utils';
import { useAtomValue } from 'jotai';
import { IcInfo } from '@yanolja-rnd/nol-icons';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import { pcWidthAtom } from '@/app/_shared/store/device';
import { SwiperNavButton } from '../../common';

export interface ProductPageProps {
  data: ConvertedProductPageWidgetData;
  initialIndex?: number;
  pageNameLog: HomeLogPageNameValue;
}

const MAX_MOBILE_SLIDE_COUNT = 5;
const MOBILE_ITEMS_PER_SLIDE = 6;
const PC_ITEMS_PER_SLIDE = 5;

const createPages = (items: VerticalProductItemProps[], maxSlideCount: number, itemsPerSlide: number) => {
  const pages: VerticalProductItemProps[][] = [];
  if (items.length < itemsPerSlide) {
    pages.push(items);
    return pages;
  }

  for (let i = 0; i < items.length; i += itemsPerSlide) {
    const slideItems = items.slice(i, i + itemsPerSlide);

    if (pages.length < maxSlideCount) {
      pages.push(slideItems);
    } else {
      break;
    }
  }

  return pages;
};

export const ProductPage: React.FC<ProductPageProps> = ({ pageNameLog, data, initialIndex = 0 }) => {
  const { items, header } = data;
  const { badge } = header || {};
  const { logMeta: widgetLogMeta } = data;
  const { isPcWidth } = useAtomValue(pcWidthAtom);
  const verticalItems: VerticalProductItemProps[] = items.map((item) => item.items).flat();
  const swiperRef = useRef<SwiperCore | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const mobileTotalSlideIndex = Math.min(
    Math.ceil(verticalItems.length / MOBILE_ITEMS_PER_SLIDE),
    MAX_MOBILE_SLIDE_COUNT,
  );
  const pcTotalSlideIndex = Math.ceil(verticalItems.length / PC_ITEMS_PER_SLIDE);

  // 화면 크기에 따라 totalSlideIndex 를 다르게 설정
  const totalSlideIndex = isPcWidth ? pcTotalSlideIndex : mobileTotalSlideIndex;
  // 화면 크기에 따라 Slide 당 item 수를 다르게 설정
  const itemsPerSlide = isPcWidth ? PC_ITEMS_PER_SLIDE : MOBILE_ITEMS_PER_SLIDE;
  // 화면 크기가 모바일인 경우, 아이템 수가 6개 미만인 경우 렌더링하지 않음
  if (!isPcWidth && verticalItems.length < MOBILE_ITEMS_PER_SLIDE) {
    return null;
  }

  // 화면 크기에 따라 한 슬라이드에 보여줄 아이템 수를 다르게 설정
  const pageItems =
    verticalItems && verticalItems.length > 0 ? createPages(verticalItems, totalSlideIndex, itemsPerSlide) : null;

  const handleClientLog = (eventType: 'click' | 'impression', pagePosition: number, item: VerticalProductItemProps) => {
    const widgetPagePosition = pagePosition + 1;
    const { id: object } = item;
    const objectType = item.logMeta?.objectType || 'ITEM';
    const { objectContainer, widgetPosition, deusGroupId } = widgetLogMeta;
    const widgetTitle = data.header?.title;
    const { widgetTargetTitle } = widgetLogMeta;
    const expMeta = data.expMeta;

    const logData: Record<string, unknown> = {
      ...item.logMeta,
      object,
      objectType,
      objectContainer,
      widgetPosition,
      widgetTitle,
      widgetTargetTitle,
      widgetPagePosition,
      deusGroupId,
      expMeta,
    };

    const filterValidValues = (data: Record<string, unknown>): Record<string, unknown> => {
      return Object.entries(data).reduce<Record<string, unknown>>((acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          acc[key] = value;
        }
        return acc;
      }, {});
    };

    // 유효한 값만 필터링
    const log = filterValidValues(logData);
    return clientLogStandardized(eventType, pageNameLog, log);
  };

  const showNavButton = verticalItems.length > PC_ITEMS_PER_SLIDE;

  // pageItems가 존재하는 경우에만 렌더링
  if (!pageItems || pageItems.length === 0) {
    return null;
  }

  return (
    <div className="my-16 w-full pc:my-40 pc:w-auto pc:max-w-pc-size">
      {!!header?.title && (
        <SubHeader
          className="px-16"
          title={header.title}
          subTitle={header.subTitle}
          {...(badge && {
            aside: (
              <>
                <span data-tooltip-id="badge-tooltip" className="cursor-pointer">
                  <ProductBadge className="whitespace-nowrap" type="outlined_basic" size="small">
                    광고
                    <IcInfo className="size-12" />
                  </ProductBadge>
                </span>
                <Tooltip id="badge-tooltip" positionStrategy="absolute" place="bottom-end" className="z-10">
                  <div>{badge?.action?.value}</div>
                </Tooltip>
              </>
            ),
          })}
        />
      )}
      <div className="relative pc:w-pc-size">
        <Swiper
          spaceBetween={isPcWidth ? 20 : 0}
          allowTouchMove
          loop
          onSlideChange={(e) => {
            setSelectedIndex(e.realIndex);
          }}
          onSwiper={(swiper: SwiperCore) => {
            swiperRef.current = swiper;
          }}>
          {/* 각 환경에 맞춰 totalSlideIndex 만큼 swiperSlide 를 그려야함*/}
          {pageItems.map((page, pageIndex) => (
            <SwiperSlide key={pageIndex}>
              <div className="items-center px-16 pc:px-0">
                <div
                  key={pageIndex}
                  className="grid grid-cols-3 grid-rows-2 gap-8 gap-y-16 pc:flex pc:flex-row pc:gap-0 pc:space-x-20">
                  {page.map((item, index) => (
                    <YaTrackerImpression key={index} onImpressed={() => handleClientLog('impression', pageIndex, item)}>
                      <div className="pc:w-[24rem]">
                        <VerticalProductItem
                          key={index}
                          {...item}
                          onClick={async (e) => {
                            e.preventDefault();
                            await waitUntilClickTimeout(handleClientLog('click', pageIndex, item));
                            if (item.action) navigateTo(item.action);
                          }}
                        />
                      </div>
                    </YaTrackerImpression>
                  ))}
                </div>
                {/* 그리드가 채워지지 않은 경우, 빈 칸을 추가 */}
                {page.length < itemsPerSlide &&
                  Array.from({ length: itemsPerSlide - page.length }).map((_, index) => <div key={`empty-${index}`} />)}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        {pageItems.length > 2 && (
          <PageController
            className="mb-16 mt-24 block pc:hidden"
            currentPage={selectedIndex < mobileTotalSlideIndex ? selectedIndex + 1 : mobileTotalSlideIndex}
            totalPages={mobileTotalSlideIndex}
            onClickPrevious={() => swiperRef.current?.slidePrev()}
            onClickNext={() => swiperRef.current?.slideNext()}
          />
        )}
        {!!showNavButton && <SwiperNavButton swiperRef={swiperRef} top={136} />}
      </div>
    </div>
  );
};
